import React, { useCallback, useMemo } from 'react';

import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Box,
  Button,
  Divider,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { getRoomBedTypeOptionsFromSchema, useRoomTypesSchema } from '~/hooks/apis/accommodation/useRoomTypesSchema';
import { useGenerateBeddingConfiguration } from '~/hooks/apis/offers/useGenerateBeddingConfiguration';

import { reportError } from '~/utils/reportError';

import type { OfferBuilderRoom } from '../types';
import { formatBedConfiguration, formatBedType, formatList } from '../utils';

interface Props {
  onChange: (nextRoomValues: Partial<OfferBuilderRoom>) => void;
  room: OfferBuilderRoom;
  syncBeddingConfigurationPackageId?: string;
}

type BedGroup = NonNullable<OfferBuilderRoom['bed_groups']>[number];
type BedConfiguration = NonNullable<BedGroup['configuration']>[number];

function parseOptionalNumber(value: string) {
  return value === '' ? null : Number(value);
}

function getDeclaredBedCount(room: OfferBuilderRoom) {
  if (Array.isArray(room.number_of_beds)) {
    return room.number_of_beds.reduce((total, count) => total + count, 0);
  }

  return room.number_of_beds ?? 0;
}

function buildDefaultBedGroups(room: OfferBuilderRoom): Array<BedGroup> {
  return [
    {
      id: 'main-bedroom',
      configuration: [
        {
          type: '',
          size: '',
          quantity: getDeclaredBedCount(room) || undefined,
        },
      ],
    },
  ];
}

function normaliseBedGroups(room: OfferBuilderRoom) {
  return room.bed_groups?.length ? room.bed_groups : buildDefaultBedGroups(room);
}

function getBedTypeOptions(bedGroups: Array<BedGroup>, schemaBedTypeOptions: Array<string>) {
  const currentBedTypes = bedGroups
    .flatMap((bedGroup) => bedGroup.configuration ?? [])
    .map((configuration) => getBedTypeValue(configuration.type, schemaBedTypeOptions));

  return Array.from(new Set(currentBedTypes.concat(schemaBedTypeOptions).filter(Boolean)));
}

function getBedTypeValue(type: string | undefined, schemaBedTypeOptions: Array<string>) {
  const formattedType = formatBedType(type);

  return schemaBedTypeOptions.find((bedType) => formatBedType(bedType) === formattedType) ?? type ?? '';
}

function getBedConfigurationRows(bedGroups: Array<BedGroup>) {
  return bedGroups.flatMap((bedGroup, bedGroupIndex) =>
    (bedGroup.configuration ?? []).map((configuration, configurationIndex) => ({
      bedGroup,
      bedGroupIndex,
      configuration,
      configurationIndex,
    })),
  );
}

function getNumberOfBeds(bedGroups: Array<BedGroup>) {
  return bedGroups.map((bedGroup) =>
    (bedGroup.configuration ?? []).reduce((total, configuration) => total + (configuration.quantity || 0), 0),
  );
}

function getBedGroupLabel(bedGroup: BedGroup, index: number, schemaBedTypeOptions: Array<string>) {
  const bedConfigurationLabels = (bedGroup.configuration ?? [])
    .map((configuration) =>
      formatBedConfiguration({
        ...configuration,
        type: getBedTypeValue(configuration.type, schemaBedTypeOptions),
      }),
    )
    .filter((label): label is string => !!label);

  return formatList(bedConfigurationLabels, 'and') || bedGroup.description?.trim() || `Bed group ${index + 1}`;
}

export default function BedsAndSizeTab({ onChange, room, syncBeddingConfigurationPackageId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: roomTypesSchema } = useRoomTypesSchema({ propertyId: room.property_id });
  const generateBeddingConfigurationMutation = useGenerateBeddingConfiguration();
  const { isPending: isGeneratingBeddingConfiguration, mutateAsync: generateBeddingConfiguration } =
    generateBeddingConfigurationMutation;
  const schemaBedTypeOptions = useMemo(() => getRoomBedTypeOptionsFromSchema(roomTypesSchema), [roomTypesSchema]);
  const bedGroups = useMemo(() => normaliseBedGroups(room), [room]);
  const bedConfigurationRows = useMemo(() => getBedConfigurationRows(bedGroups), [bedGroups]);
  const bedTypeOptions = useMemo(
    () => getBedTypeOptions(bedGroups, schemaBedTypeOptions),
    [bedGroups, schemaBedTypeOptions],
  );
  const bedCountsByGroup = useMemo(() => getNumberOfBeds(bedGroups), [bedGroups]);
  const configuredBedCount = bedCountsByGroup.reduce((total, count) => total + count, 0);
  const declaredBedCount = getDeclaredBedCount(room) || configuredBedCount;
  const hasBedCountMismatch = declaredBedCount !== configuredBedCount;

  const updateBedGroups = (nextBedGroups: Array<BedGroup>, numberOfBedrooms = room.number_of_bedrooms) => {
    onChange({
      bed_groups: nextBedGroups,
      number_of_beds: getNumberOfBeds(nextBedGroups),
      number_of_bedrooms: numberOfBedrooms,
    });
  };

  const updateBedConfiguration = (
    bedGroupIndex: number,
    configurationIndex: number,
    nextConfiguration: BedConfiguration,
  ) => {
    updateBedGroups(
      bedGroups.map((bedGroup, currentBedGroupIndex) =>
        currentBedGroupIndex === bedGroupIndex
          ? {
              ...bedGroup,
              configuration: (bedGroup.configuration ?? []).map((configuration, currentConfigurationIndex) =>
                currentConfigurationIndex === configurationIndex ? nextConfiguration : configuration,
              ),
            }
          : bedGroup,
      ),
    );
  };

  const buildDefaultBedConfiguration = () => ({ type: schemaBedTypeOptions[0] ?? '', size: '', quantity: 1 });

  const addBedConfiguration = (bedGroupIndex: number) => {
    const nextConfiguration = buildDefaultBedConfiguration();
    const nextBedGroups = bedGroups.map((bedGroup, index) =>
      index === bedGroupIndex
        ? { ...bedGroup, configuration: (bedGroup.configuration ?? []).concat(nextConfiguration) }
        : bedGroup,
    );

    updateBedGroups(nextBedGroups, Math.max(room.number_of_bedrooms ?? 0, nextBedGroups.length));
  };

  const addBedGroup = () => {
    const nextBedGroups = bedGroups.concat({
      id: `bed-group-${Date.now()}`,
      configuration: [buildDefaultBedConfiguration()],
    });

    updateBedGroups(nextBedGroups, nextBedGroups.length);
  };

  const deleteBedConfiguration = (bedGroupIndex: number, configurationIndex: number) => {
    const nextBedGroups = bedGroups
      .map((bedGroup, currentBedGroupIndex) =>
        currentBedGroupIndex === bedGroupIndex
          ? {
              ...bedGroup,
              configuration: (bedGroup.configuration ?? []).filter(
                (_configuration, currentConfigurationIndex) => currentConfigurationIndex !== configurationIndex,
              ),
            }
          : bedGroup,
      )
      .filter((bedGroup) => bedGroup.configuration?.length);

    updateBedGroups(nextBedGroups, nextBedGroups.length);
  };

  const syncBeddingConfiguration = useCallback(
    async (suppressNotifications = false) => {
      if (!syncBeddingConfigurationPackageId) {
        if (!suppressNotifications) {
          enqueueSnackbar('No matching package found for this room type', { variant: 'warning' });
        }
        return;
      }

      try {
        const response = await generateBeddingConfiguration(syncBeddingConfigurationPackageId);
        const syncedBedGroups = (response.result?.bed_grouping ?? []).map((bedGroup, index) => {
          const configuration = bedGroup.map((bedConfiguration) => ({
            quantity: bedConfiguration.quantity ?? 0,
            size: '',
            type: bedConfiguration.bed_type,
          }));

          return {
            id: `synced-bedroom-${index + 1}`,
            configuration,
          };
        });

        if (syncedBedGroups.length === 0) {
          if (!suppressNotifications) {
            enqueueSnackbar('No bedding configuration was generated for this room type', { variant: 'warning' });
          }
          return;
        }

        onChange({
          bed_groups: syncedBedGroups,
          number_of_beds: getNumberOfBeds(syncedBedGroups),
          number_of_bedrooms: syncedBedGroups.length,
        });
        if (!suppressNotifications) {
          enqueueSnackbar(`Bedding configuration generated successfully for ${room.name || 'room'}`, {
            variant: 'success',
          });
        }
      } catch (error) {
        if (!suppressNotifications) {
          enqueueSnackbar(
            `Failed to generate bedding configuration for ${room.name || 'room'}: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
            { variant: 'error' },
          );
          reportError(error);
        }
      }
    },
    [enqueueSnackbar, generateBeddingConfiguration, onChange, room.name, syncBeddingConfigurationPackageId],
  );

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        <Stack spacing={1}>
          <FormLabel>Size (m²)</FormLabel>
          <TextField
            type="number"
            size="small"
            value={room.size_sqm ?? ''}
            onChange={(event) => onChange({ size_sqm: parseOptionalNumber(event.target.value) })}
          />
        </Stack>
        <Stack spacing={1}>
          <FormLabel>Bedrooms</FormLabel>
          <TextField
            type="number"
            size="small"
            value={room.number_of_bedrooms ?? ''}
            onChange={(event) => onChange({ number_of_bedrooms: parseOptionalNumber(event.target.value) })}
          />
        </Stack>
        <Stack spacing={1}>
          <FormLabel>Bathrooms</FormLabel>
          <TextField
            type="number"
            size="small"
            value={room.number_of_bathrooms ?? ''}
            onChange={(event) => onChange({ number_of_bathrooms: parseOptionalNumber(event.target.value) })}
          />
        </Stack>
      </Box>

      <Stack spacing={1}>
        <FormLabel required>Number of beds</FormLabel>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          {bedCountsByGroup.map((bedCount, index) => (
            <TextField
              key={index}
              required
              type="number"
              size="small"
              label={`Beds in group ${index + 1}`}
              value={bedCount || ''}
              error={bedCount <= 0 || hasBedCountMismatch}
              helperText={
                hasBedCountMismatch && index === 0 ? `Bed configuration total is ${configuredBedCount}` : undefined
              }
              slotProps={{ input: { readOnly: true } }}
            />
          ))}
        </Box>
      </Stack>

      <Stack spacing={1.5}>
        <FormLabel required>Bed groups</FormLabel>
        {bedGroups.map((bedGroup, bedGroupIndex) => (
          <React.Fragment key={bedGroup.id || bedGroupIndex}>
            {bedGroupIndex > 0 && <Divider>OR</Divider>}
            <Stack spacing={1.25} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle2">
                {getBedGroupLabel(bedGroup, bedGroupIndex, schemaBedTypeOptions)}
              </Typography>
              {(bedGroup.configuration ?? []).map((configuration, configurationIndex) => {
                const isBedConfigurationMissing = !configuration.type?.trim();

                return (
                  <Stack key={`${bedGroup.id || bedGroupIndex}-${configurationIndex}`} spacing={0.5}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'minmax(0, 1fr) 92px 36px', md: 'minmax(0, 1fr) 120px 36px' },
                        gap: 1.5,
                        alignItems: 'center',
                      }}
                    >
                      <TextField
                        select
                        size="small"
                        required
                        label="Bed configuration"
                        value={getBedTypeValue(configuration.type, schemaBedTypeOptions)}
                        error={isBedConfigurationMissing}
                        onChange={(event) =>
                          updateBedConfiguration(bedGroupIndex, configurationIndex, {
                            ...configuration,
                            type: event.target.value,
                          })
                        }
                      >
                        <MenuItem value="" sx={{ display: 'none' }} />
                        {bedTypeOptions.map((bedType) => (
                          <MenuItem key={bedType} value={bedType}>
                            {formatBedType(bedType)}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        type="number"
                        size="small"
                        label="Quantity"
                        slotProps={{ htmlInput: { min: 0 } }}
                        value={configuration.quantity ?? ''}
                        onChange={(event) =>
                          updateBedConfiguration(bedGroupIndex, configurationIndex, {
                            ...configuration,
                            quantity: Number(event.target.value) || 0,
                          })
                        }
                      />
                      <IconButton
                        color="error"
                        aria-label="Delete bed configuration"
                        disabled={bedConfigurationRows.length <= 1}
                        onClick={() => deleteBedConfiguration(bedGroupIndex, configurationIndex)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {isBedConfigurationMissing && <FormHelperText error>Bed configuration is required</FormHelperText>}
                  </Stack>
                );
              })}
              <Box>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addBedConfiguration(bedGroupIndex)}
                >
                  Add bed
                </Button>
              </Box>
            </Stack>
          </React.Fragment>
        ))}
        <Box>
          <Button variant="text" startIcon={<AddIcon />} onClick={addBedGroup}>
            Add bed group
          </Button>
        </Box>
      </Stack>

      <Box>
        <Button
          variant="outlined"
          startIcon={<SyncIcon />}
          disabled={isGeneratingBeddingConfiguration}
          onClick={() => void syncBeddingConfiguration()}
        >
          {isGeneratingBeddingConfiguration ? 'Syncing...' : 'Sync bedding configuration'}
        </Button>
      </Box>
    </Stack>
  );
}
